import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Ticket, TicketStage, TicketPriority, TicketFilter } from '@/types/tickets';
import TicketCard from './TicketCard';
import TicketFilterMenu from './TicketFilterMenu';
import { isAssignedToMe as isAssignedToMeShared } from '@/lib/ticket-utils';
import { matchesTicket, isFilterActive } from '@/lib/ticket-filter';

interface TicketKanbanColumnProps {
  stage: TicketStage;
  tickets: Ticket[];
  onTicketClick: (ticket: Ticket) => void;
  searchTerm?: string;
  currentUserId?: number | null;
  filter: TicketFilter;
  onFilterChange: (filter: TicketFilter) => void;
  globalFilter: TicketFilter;
}

const PRIORITY_ORDER: Record<TicketPriority, number> = {
  [TicketPriority.URGENT]: 0,
  [TicketPriority.HIGH]: 1,
  [TicketPriority.MEDIUM]: 2,
  [TicketPriority.LOW]: 3
};

const TicketKanbanColumn: React.FC<TicketKanbanColumnProps> = ({
  stage,
  tickets,
  onTicketClick,
  searchTerm = '',
  currentUserId = null,
  filter,
  onFilterChange,
  globalFilter,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage-${stage.id}`
  });

  const normalizedUserId = currentUserId ? Number(currentUserId) : null;

  // Atribuído a mim = principal OU co-responsável
  const isAssignedToMe = (ticket: Ticket): boolean =>
    isAssignedToMeShared(ticket, normalizedUserId);

  // Filtrar: busca + filtro global + filtro desta coluna (combinação E)
  let filteredTickets = tickets.filter(ticket =>
    matchesTicket(ticket, {
      searchTerm,
      globalFilter,
      columnFilter: filter,
      currentUserId: normalizedUserId,
    })
  );

  // Ordenar tickets: primeiro os atribuídos a mim, depois os outros
  filteredTickets = filteredTickets.sort((a, b) => {
    const aAssignedToMe = isAssignedToMe(a);
    const bAssignedToMe = isAssignedToMe(b);

    // Se um está atribuído a mim e o outro não, o atribuído a mim vem primeiro
    if (aAssignedToMe && !bAssignedToMe) return -1;
    if (!aAssignedToMe && bAssignedToMe) return 1;

    // Se ambos estão atribuídos a mim ou ambos não estão
    if (aAssignedToMe && bAssignedToMe) {
      const priorityDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      if (priorityDiff !== 0) return priorityDiff;

      const aDate = new Date(a.moved_to_stage_at || a.created_at || 0).getTime();
      const bDate = new Date(b.moved_to_stage_at || b.created_at || 0).getTime();
      return aDate - bDate;
    }

    const priorityDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    if (priorityDiff !== 0) return priorityDiff;

    const aDate = new Date(a.moved_to_stage_at || a.created_at || 0).getTime();
    const bDate = new Date(b.moved_to_stage_at || b.created_at || 0).getTime();
    return aDate - bDate;
  });

  const hasActiveFilter = isFilterActive(filter);
  const anyFilterActive = hasActiveFilter || isFilterActive(globalFilter) || Boolean(searchTerm);

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-80 transition-all ${isOver ? 'opacity-75' : ''}`}
    >
      <Card className={`h-full border-t-4`} style={{ borderTopColor: stage.color }}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base font-semibold flex-1">
              {stage.name}
            </CardTitle>
            <div className="flex items-center gap-2">
              <TicketFilterMenu filter={filter} onChange={onFilterChange} />
              <Badge
                variant="secondary"
                className="rounded-full"
                style={{
                  backgroundColor: `${stage.color}20`,
                  color: stage.color
                }}
              >
                {filteredTickets.length}
              </Badge>
            </div>
          </div>
          {stage.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {stage.description}
            </p>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          <SortableContext items={filteredTickets.map(t => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
              {filteredTickets.map(ticket => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  onClick={() => onTicketClick(ticket)}
                />
              ))}

              {filteredTickets.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {anyFilterActive ? 'Nenhum ticket encontrado' : 'Nenhum ticket nesta etapa'}
                </div>
              )}
            </div>
          </SortableContext>
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketKanbanColumn;
